import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Shield, ShieldAlert, CheckCircle2, XCircle, Clock, Ban, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { api, apiErr } from "@/lib/api";

const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;

function Stat({ label, value }) {
  return (
    <div className="rounded-2xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] p-4">
      <div className="text-xs uppercase tracking-wide text-[color:var(--rb-text-faint)]">{label}</div>
      <div className="rb-display mt-1 text-2xl font-semibold text-[color:var(--rb-text)]">{value}</div>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [skills, setSkills] = useState([]);
  const [txns, setTxns] = useState({ purchases: [], payouts: [] });
  const [queue, setQueue] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadOrgs = () => api.get("/admin/orgs").then(({ data }) => setOrgs(data));

  useEffect(() => {
    if (user && !user.is_owner) {
      navigate("/build", { replace: true });
      return;
    }
    (async () => {
      try {
        const [a, b, c, q, o, f] = await Promise.all([
          api.get("/admin/stats"),
          api.get("/admin/skills"),
          api.get("/admin/transactions"),
          api.get("/admin/review-queue"),
          api.get("/admin/orgs"),
          api.get("/admin/funnel"),
        ]);
        setStats(a.data);
        setSkills(b.data);
        setTxns(c.data);
        setQueue(q.data);
        setOrgs(o.data);
        setFunnel(f.data);
      } catch (e) {
        console.error("[admin] failed to load owner console data", e);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const toggleSuspend = async (org) => {
    const suspended = org.status === "suspended";
    try {
      await api.post(`/admin/orgs/${org.slug}/${suspended ? "unsuspend" : "suspend"}`);
      toast.success(suspended ? `${org.slug} restored` : `${org.slug} suspended`);
      loadOrgs();
    } catch (e) {
      toast.error(apiErr(e, "Action failed"));
    }
  };

  const reviewSkill = async (id, action) => {
    try {
      await api.post(`/admin/skills/${id}/review`, { action });
      toast.success(action === "approve" ? "Skill approved" : "Skill rejected");
      const [b, q] = await Promise.all([api.get("/admin/skills"), api.get("/admin/review-queue")]);
      setSkills(b.data);
      setQueue(q.data);
    } catch (e) {
      toast.error("Review action failed");
    }
  };

  const pendingCount = queue.filter((s) => ["pending", "appealed"].includes(s.review_status)).length;

  if (!user?.is_owner) {
    return <AppShell><div className="py-24 text-center text-[color:var(--rb-text-faint)]">Owner access required.</div></AppShell>;
  }
  if (loading) {
    return <AppShell><div className="flex justify-center py-24 text-[color:var(--rb-text-faint)]"><Loader2 className="h-6 w-6 animate-spin" /></div></AppShell>;
  }

  return (
    <AppShell>
      <div className="mb-6 flex items-center gap-2">
        <Shield className="h-5 w-5 text-[color:var(--rb-accent)]" />
        <h1 className="rb-display text-2xl font-semibold tracking-tight text-[color:var(--rb-text)]">Owner console</h1>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Stat label="Skills" value={stats?.total_skills || 0} />
        <Stat label="Public" value={stats?.public_skills || 0} />
        <Stat label="Users" value={stats?.total_users || 0} />
        <Stat label="Sales" value={stats?.total_sales || 0} />
        <Stat label="Platform fees" value={money(stats?.platform_fee_cents)} />
        <Stat label="Creator payouts" value={money(stats?.creator_payouts_cents)} />
      </div>

      {/* GTM funnel (docs/GTM.md §5) — WSE is the north star. */}
      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8" data-testid="funnel-stats">
        <Stat label="WSE ★" value={funnel?.wse ?? "—"} />
        <Stat label="Signups" value={funnel?.signups_total ?? "—"} />
        <Stat label="Signups 7d" value={funnel?.signups_7d ?? "—"} />
        <Stat label="Orgs" value={funnel?.orgs_total ?? "—"} />
        <Stat label="Orgs 7d" value={funnel?.orgs_7d ?? "—"} />
        <Stat label="Activated" value={funnel?.activated_orgs ?? "—"} />
        <Stat label="Paying orgs" value={funnel?.paying_orgs ?? "—"} />
        <Stat label="Paid seats" value={funnel?.paid_seats ?? "—"} />
      </div>

      <Tabs defaultValue="skills" className="mt-6">
        <TabsList className="bg-[color:var(--rb-surface)]">
          <TabsTrigger value="skills" className="data-[state=active]:bg-[color:var(--rb-accent)] data-[state=active]:text-[color:var(--rb-void)]">Skills</TabsTrigger>
          <TabsTrigger value="review" data-testid="tab-review-queue" className="data-[state=active]:bg-[color:var(--rb-accent)] data-[state=active]:text-[color:var(--rb-void)]">
            Review Queue{pendingCount ? ` (${pendingCount})` : ""}
          </TabsTrigger>
          <TabsTrigger value="txns" className="data-[state=active]:bg-[color:var(--rb-accent)] data-[state=active]:text-[color:var(--rb-void)]">Transactions</TabsTrigger>
          <TabsTrigger value="orgs" data-testid="tab-orgs" className="data-[state=active]:bg-[color:var(--rb-accent)] data-[state=active]:text-[color:var(--rb-void)]">Orgs</TabsTrigger>
        </TabsList>

        <TabsContent value="skills" className="mt-4 overflow-x-auto rounded-2xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] p-4">
          <Table>
            <TableHeader>
              <TableRow className="border-[color:var(--rb-border)]">
                <TableHead className="text-[color:var(--rb-text-faint)]">Slug</TableHead>
                <TableHead className="text-[color:var(--rb-text-faint)]">Creator</TableHead>
                <TableHead className="text-[color:var(--rb-text-faint)]">Category</TableHead>
                <TableHead className="text-[color:var(--rb-text-faint)]">Public</TableHead>
                <TableHead className="text-[color:var(--rb-text-faint)]">Rating</TableHead>
                <TableHead className="text-[color:var(--rb-text-faint)]">Downloads</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {skills.map((s) => (
                <TableRow key={s.id} className="border-[color:var(--rb-border)]">
                  <TableCell className="rb-mono text-[color:var(--rb-text)]">{s.slug}</TableCell>
                  <TableCell className="text-[color:var(--rb-text-dim)]">{s.creator_name}</TableCell>
                  <TableCell className="text-[color:var(--rb-text-dim)]">{s.category}</TableCell>
                  <TableCell>{s.is_public ? <Badge className="bg-[color:rgba(45,212,191,0.14)] text-[color:var(--rb-accent)]">Public</Badge> : <Badge variant="outline" className="border-[color:var(--rb-border)] text-[color:var(--rb-text-faint)]">Private</Badge>}</TableCell>
                  <TableCell className="text-[color:var(--rb-text-dim)]">{(s.avg_rating || 0).toFixed(1)} ({s.review_count || 0})</TableCell>
                  <TableCell className="text-[color:var(--rb-text-dim)]">{s.download_count || 0}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TabsContent>

        <TabsContent value="review" className="mt-4" data-testid="review-queue-panel">
          {queue.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-[color:var(--rb-border)] py-16 text-center text-sm text-[color:var(--rb-text-faint)]">
              <ShieldAlert className="mx-auto mb-2 h-6 w-6 text-[color:var(--rb-accent)]" /> No uploaded skills awaiting review.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {queue.map((s) => {
                const reasons = s.scan?.reasons || [];
                const verdict = s.scan?.llm?.verdict;
                return (
                  <div key={s.id} className="rounded-2xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] p-4" data-testid="review-queue-item">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="rb-mono truncate text-sm font-semibold text-[color:var(--rb-text)]">{s.slug}</div>
                        <div className="text-xs text-[color:var(--rb-text-faint)]">by {s.creator_name} · {s.category}</div>
                      </div>
                      {s.review_status === "appealed"
                        ? <Badge className="gap-1 bg-[color:var(--rb-gold-soft)] text-[color:var(--rb-gold)]"><Clock className="h-3 w-3" /> Appealed</Badge>
                        : s.review_status === "rejected"
                        ? <Badge className="gap-1 bg-[color:rgba(248,113,113,0.14)] text-[color:#f87171]"><XCircle className="h-3 w-3" /> Auto-rejected</Badge>
                        : <Badge className="gap-1 bg-[color:var(--rb-gold-soft)] text-[color:var(--rb-gold)]"><Clock className="h-3 w-3" /> Pending</Badge>}
                    </div>
                    <p className="mt-2 line-clamp-2 text-sm text-[color:var(--rb-text-dim)]">{s.short_description || "—"}</p>
                    {s.appeal ? (
                      <p className="mt-2 rounded-xl border border-[color:rgba(212,175,55,0.35)] bg-[color:var(--rb-gold-soft)] p-2.5 text-xs text-[color:var(--rb-text-dim)]" data-testid="appeal-note">
                        <span className="font-medium text-[color:var(--rb-gold)]">Creator’s appeal:</span> {s.appeal.message || "(no message)"}
                      </p>
                    ) : null}
                    <div className="mt-3 rounded-xl border border-[color:var(--rb-border)] bg-[color:var(--rb-surface)] p-2.5">
                      <div className="flex items-center gap-2 text-xs">
                        <span className="text-[color:var(--rb-text-faint)]">AI verdict:</span>
                        <span className={verdict === "malicious" ? "text-[color:#f87171]" : verdict === "safe" ? "text-[color:var(--rb-accent)]" : "text-[color:var(--rb-gold)]"}>{verdict || "n/a"}</span>
                      </div>
                      {reasons.length > 0 && (
                        <ul className="mt-1.5 list-inside list-disc text-[11px] text-[color:var(--rb-text-faint)]">
                          {reasons.slice(0, 5).map((r, i) => <li key={i} className="truncate">{r}</li>)}
                        </ul>
                      )}
                    </div>
                    <div className="mt-3 flex gap-2">
                      <Button size="sm" onClick={() => reviewSkill(s.id, "approve")} data-testid="review-approve-button"
                        className="flex-1 rounded-xl bg-[color:var(--rb-accent)] text-[color:var(--rb-void)] hover:bg-[color:var(--rb-accent-2)]">
                        <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => reviewSkill(s.id, "reject")} data-testid="review-reject-button"
                        className="flex-1 rounded-xl border-[color:rgba(248,113,113,0.4)] text-[color:#f87171] hover:bg-[color:rgba(248,113,113,0.08)]">
                        <XCircle className="mr-1.5 h-3.5 w-3.5" /> Reject
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="txns" className="mt-4 overflow-x-auto rounded-2xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] p-4">
          <Table>
            <TableHeader>
              <TableRow className="border-[color:var(--rb-border)]">
                <TableHead className="text-[color:var(--rb-text-faint)]">Skill</TableHead>
                <TableHead className="text-[color:var(--rb-text-faint)]">Buyer</TableHead>
                <TableHead className="text-[color:var(--rb-text-faint)]">Amount</TableHead>
                <TableHead className="text-[color:var(--rb-text-faint)]">Creator</TableHead>
                <TableHead className="text-[color:var(--rb-text-faint)]">Platform</TableHead>
                <TableHead className="text-[color:var(--rb-text-faint)]">Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {txns.purchases.map((p) => (
                <TableRow key={p.id} className="border-[color:var(--rb-border)]">
                  <TableCell className="rb-mono text-[color:var(--rb-text)]">{p.skill_name}</TableCell>
                  <TableCell className="text-[color:var(--rb-text-dim)]">{p.buyer_name || p.buyer_id}</TableCell>
                  <TableCell className="text-[color:var(--rb-text-dim)]">{money(p.amount_cents)}</TableCell>
                  <TableCell className="text-[color:var(--rb-gold)]">{money(p.creator_payout_cents)}</TableCell>
                  <TableCell className="text-[color:var(--rb-text-dim)]">{money(p.platform_fee_cents)}</TableCell>
                  <TableCell className="text-[color:var(--rb-text-faint)]">{(p.created_at || "").slice(0, 10)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TabsContent>

        <TabsContent value="orgs" className="mt-4 overflow-x-auto rounded-2xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] p-4" data-testid="orgs-panel">
          <Table>
            <TableHeader>
              <TableRow className="border-[color:var(--rb-border)]">
                <TableHead className="text-[color:var(--rb-text-faint)]">Slug</TableHead>
                <TableHead className="text-[color:var(--rb-text-faint)]">Name</TableHead>
                <TableHead className="text-[color:var(--rb-text-faint)]">Plan</TableHead>
                <TableHead className="text-[color:var(--rb-text-faint)]">Seats</TableHead>
                <TableHead className="text-[color:var(--rb-text-faint)]">Status</TableHead>
                <TableHead className="text-[color:var(--rb-text-faint)]">Artifacts</TableHead>
                <TableHead className="text-[color:var(--rb-text-faint)]">Created</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orgs.map((o) => (
                <TableRow key={o.id} data-testid="org-row" className="border-[color:var(--rb-border)]">
                  <TableCell className="rb-mono text-[color:var(--rb-text)]">{o.slug}</TableCell>
                  <TableCell className="text-[color:var(--rb-text-dim)]">{o.name}</TableCell>
                  <TableCell>
                    {o.plan === "team"
                      ? <Badge className="bg-[color:rgba(45,212,191,0.14)] text-[color:var(--rb-accent)]">Team</Badge>
                      : <Badge variant="outline" className="border-[color:var(--rb-border)] text-[color:var(--rb-text-faint)]">Free</Badge>}
                  </TableCell>
                  <TableCell className="text-[color:var(--rb-text-dim)]">{o.seats_in_use} / {o.seats}</TableCell>
                  <TableCell>
                    {o.status === "active"
                      ? <Badge className="bg-[color:rgba(45,212,191,0.14)] text-[color:var(--rb-accent)]">Active</Badge>
                      : o.status === "suspended"
                      ? <Badge className="gap-1 bg-[color:rgba(248,113,113,0.14)] text-[color:#f87171]"><Ban className="h-3 w-3" /> Suspended</Badge>
                      : <Badge className="bg-[color:var(--rb-gold-soft)] text-[color:var(--rb-gold)]">{o.status}</Badge>}
                  </TableCell>
                  <TableCell className="text-[color:var(--rb-text-dim)]">{o.published_count} / {o.artifact_count}</TableCell>
                  <TableCell className="text-[color:var(--rb-text-faint)]">{(o.created_at || "").slice(0, 10)}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" onClick={() => toggleSuspend(o)} data-testid="suspend-org-button"
                      className={o.status === "suspended"
                        ? "gap-1 rounded-xl border-[color:rgba(45,212,191,0.4)] text-[color:var(--rb-accent)] hover:bg-[color:rgba(45,212,191,0.08)]"
                        : "gap-1 rounded-xl border-[color:rgba(248,113,113,0.4)] text-[color:#f87171] hover:bg-[color:rgba(248,113,113,0.08)]"}>
                      {o.status === "suspended" ? <><PlayCircle className="h-3.5 w-3.5" /> Restore</> : <><Ban className="h-3.5 w-3.5" /> Suspend</>}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {orgs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-xs text-[color:var(--rb-text-faint)]">No orgs yet.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TabsContent>
      </Tabs>
    </AppShell>
  );
}
